import {
  Text,
  TextInput,
  Select,
  Chip,
  Checkbox,
  Badge,
  NumberInput,
  Group,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import React, { useState, useEffect, useMemo } from "react";
import { PageList } from "../../components/pageList";
import {
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import {
  IconTrash,
  IconPencil,
  IconSettings,
  IconEye,
  IconCopy,
  IconPhone,
} from "@tabler/icons";
import ActionCellRender from "../../components/actionCellRender";
import Chips from "../../components/chips";
import moment from "moment";
import {
  DATA_COUNTRY,
  DATA_EDUCATION_LEVEL,
  DATA_LANGUAGE,
  DATA_NATIONALITY,
  DATA_SALUATATION,
  DATA_ID_TYPE,
  DATA_CLIENT_TYPES,
  DATA_GENDERS,
  DATA_MARIAGE_STATUS,
} from "../../data/options";
import { useTranslation } from "react-i18next";
import RemoteSelect2 from "../../components/remoteSelect2";
import ConsultantList from "../../components/consultantList";
import ClientBasicInfo from "../../components/clientBasicInfo";
import ClientActionList from "../../components/clientActionList";
import ClientOrderHistory from "../../components/clientOrderHistory";
import ClientOrderSummary from "../../components/clientOrderSummary";
import ClientRiskStats from "../../components/clientRiskStats";
import ClientAMLList from "../../components/clientAMLList";
import ClientRPQList from "../../components/clientRPQList";
import ClientRpqAlert from "../../components/clientRpqAlert";

import ClientPageStats from "../../components/clientPageStats";
import AddressList from "../../components/addressList";
import PhoneNumberList from "../../components/phoneNumberList";
import EmailList from "../../components/emailList";
import ContactList from "../../components/contactList";
import ClientIdentityList from "../../components/clientIdentityList";
import NameCard from "../../components/nameCard";
import ActivityLog from "../../components/activityLog.js";
import FollowupActionTable from "../../components/followupActionTable.js";
import UniqueTextInput from "../../components/uniqueTextInput";
import { DEFAULT_PHONE } from "../../helpers/phoneUtils";

import FormConfirmation from "../../components/formConfirmation";
import { useCellRender } from "../../hooks/useCellRender";

import ClientDebitNoteHistory from "../../components/clientDebitNoteHistory";

import FileList, { K_FILE_LIST_DISPLAY_MODE } from "../../components/fileList";
import { IMAGE_MIME_TYPE } from "@mantine/dropzone";
import UserFileGroupList from "../../components/userFileGroupList";
import { useAuthHeader, useAuthUser } from "react-auth-kit";

import { authName } from "../../App";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useLocation } from "react-router-dom";
import RemarkList from "../../components/remarkList";
import ClientMajorInvestmentPortfolio from "../../components/clientMajorInvestmentPortfolio";
import ClientIncomeSource from "../../components/clientIncomeSource";
import ClientAnnualIncome from "../../components/clientAnnualIncome";
import ClientCurrentAssets from "../../components/clientCurrentAssets";
import { RiskControlSettingsProvider } from "../../contexts/riskControlSettingsContext";
import { useAccessRight } from "../../hooks/useAccessRight";
import _ from "lodash";
import ClientCreditTransaction from "../../components/clientCreditTransaction";
import ClientRelationshipList from "../../components/clientRelationshipList";
import { useServerApi } from "../../hooks/userServerApi";
import {
  buildIndividualClientValidate,
  buildClientNameValidate,
  CLIENT_CONFIRM_FIELD_LABELS,
} from "../../helpers/clientFormValidate";
import { applyDefaultIdIssueCountry } from "../../helpers/clientIdIssueCountry";
import {
  isIdExpiryRequired,
  isMainIdRequired,
  isOtherIdsSectionVisible,
  validateClientIdFormat,
} from "../../helpers/clientIdentity";
import NoIdProvidedCheckbox from "../../components/noIdProvidedCheckbox";
import { useExtraFieldsFormSchema } from "../../hooks/useExtraFieldsFormSchema";
import { formatTeamUserLabel } from "../../helpers/consultantTeamFilter";
import { McaApprovalListBadge } from "../../components/mca/McaEntityIndicators";

const apiEntity = "client";
const clientImportTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/client/downloadTemplate`;
const clientLogImportTemplateUrl = `${process.env.REACT_APP_SERVER_URL}/client/downloadLogTemplate`;
const initialValues = {
  surname: "",
  givenName: "",
  name: "",
  cname: "",
  type: "INDIVIDUAL",
  idType: "HKID",
  idIssueCountry: "Hong Kong",
  otherIds: [],
  extraFieldValues: {},
  address: {
    country: "Hong Kong",
  },
  consultants: [],
  language: "CANTONESE",
  idIsUnqiue: false,
  pep: {
    isPep: false,
    remark: "",
  },
  professionalInvestor: false,
  piEffectiveDate: null,
  piAnnualConfirmationDate: null,
  piExpirationReminderDate: null,
  piTerminationDate: null,
  gender: "Male",
  maritalStatus: "Single",
  educationLevel: "",
  nationality: "Chinese",
  residentialCountry: "Hong Kong",
  placeOfBirth: "Hong Kong",
  birthday: new Date("1900-01-01"),
  occupation: "Other",
  status: process.env.REACT_APP_CLIENT_INIT_STATUS ?? "ACTIVE",
  phone: { ...DEFAULT_PHONE },
  phones: [],
  emails: [],
  files: [],
  specimenSignatureFiles: [],
  consultantFiles: [],
  fileGroups: [],
};

export default function Client({ role = "OPERATION" }) {
  const [cellRender] = useCellRender();
  const [preQuery, setPreQuery] = useState(null);
  const auth = useAuthUser();
  const tabNav = useTabNavigate();
  const { t } = useTranslation();
  const [getAccessRight, accessRight] = useAccessRight();
  const [api] = useServerApi();

  const userRoleName = auth().userRole?.name?.toUpperCase() ?? "";
  const { pathname: currentURL } = useLocation();

  const isConsultant = currentURL.includes("consultant");

  const editFormSchema = {
    title: "Client",
    titleField: "name",

    display: {
      mode: ENUM_FORM_DISPLAY.LINK,
      size: "xl", //xs, sm, xl  ....100%
      // position:"right"
    },
    apiEntity,
    initialValues,

    validate: {
      code: (value) =>
        value?.length < 2 ? "Code must have at least 2 letters" : null,
      ...buildClientNameValidate(t),
      id: (value, formValues) =>
        validateClientIdFormat(value, formValues.idType, t),
    },

    layout: {
      containers: [
        {
          key: "main-g",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            {
              key: "main-g1",
              props: {
                span: 3,
              },
            },
            {
              key: "main-g2",
              props: {
                span: 9,
              },
            },
          ],
        },

        {
          key: "section-consultant-info",
          parent: "main-g1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Consultant",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            minHeight: "0px",
            mt: 0,
            mb: 20,
          },
        },

        {
          key: "section-action-list",
          parent: "main-g1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            // title: "Action List",
            // titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            minHeight: "0px",
            mt: 0,
            mb: 20,
          },
        },
        {
          key: "section-name-card",
          parent: "main-g1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Name Card",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            minHeight: "0px",
            mt: 0,
            mb: 20,
          },
        },

        {
          key: "main-tab",
          parent: "main-g2",
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Overview",
          },
          tab: [
            {
              key: "tab-overview",
              label: "Overview",
              icon: IconSettings,
            },
            {
              key: "tab-order-summary",
              label: "Order Summary",
              icon: IconSettings,
              showTab: (values) => !!values._id,
            },
            // {
            // 	key: "tab-orders",
            // 	label: "Orders",
            // 	icon: IconSettings,
            // },
            {
              key: "tab-relationships",
              label: "Relationships",
              icon: IconSettings,
            },
            {
              key: "tab-risk",
              label: "Risk Control",
              icon: IconSettings,
            },
            {
              key: "tab-files",
              label: "Files",
              icon: IconSettings,
            },
            {
              key: "tab-log",
              label: "Logs",
              icon: IconSettings,
            },
          ],
        },
        {
          key: "section-alert",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.EMPTY,
          props: {
            withBorder: false,
            minHeight: 0,
            mt: 20,
            mb: 10,
          },
        },
        {
          key: "section-basic-info",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Basic Info",
            // titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            mt: 0,
            mb: 20,
          },
        },
        {
          key: "section-order-summary",
          parent: "tab-order-summary",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "",
            withBorder: false,
            mt: 0,
            mb: 20,
          },
        },
        {
          key: "section-contact-info",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Contact Info",
            withBorder: false,
            mt: 0,
            mb: 20,
          },
        },
        {
          key: "tab-contact-info",
          parent: "section-contact-info",
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "outline",
            size: "sm",
            defaultValue: "Phone Numbers",
          },
          tab: [
            {
              key: "tab-phones",
              label: "Phone Numbers",
            },
            {
              key: "tab-emails",
              label: "Emails",
            },
            {
              key: "tab-addresses",
              label: "Address",
            },
            {
              key: "tab-related-persons",
              label: "Related Person Info",
            },
          ],
        },

        {
          key: "tab-history",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            defaultValue: "Debit Notes",
          },
          tab: [
            {
              key: "tab-debit-notes",
              label: "Debit Notes",
            },
            {
              key: "tab-credit-transaction",
              label: "Credit Transaction",
            },
          ],
        },
        {
          key: "section-order-history",
          parent: "tab-order-summary",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Orders",
            withBorder: false,
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "section-debit-note-history",
          parent: "tab-debit-notes",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "section-credit-transaction",
          parent: "tab-credit-transaction",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "section-remarks",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Client Remarks",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            minHeight: 0,
            mt: 20,
            mb: 20,
          },
        },

        {
          key: "section-followup",
          parent: "tab-overview",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Followup Tickets",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,

            mt: 20,
            mb: 20,
          },
        },

        {
          key: "section-client-log",
          parent: "tab-log",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Client Log",
            titleOrder: 5,
            // description: "Please assign the corresponding consultant",
            withBorder: false,
            minHeight: 350,
            mt: 20,
            mb: 20,
          },
        },

        {
          key: "section-files",
          parent: "tab-files",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: () => role !== "CONSULTANT",
          props: {
            title: "Files",
            titleOrder: 5,
            withBorder: false,
            minHeight: "0px",
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "section-specimen-signature",
          parent: "tab-files",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: () => role !== "CONSULTANT",
          props: {
            title: "Specimen Signature",
            titleOrder: 5,
            withBorder: false,
            minHeight: "0px",
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "section-consultant-files",
          parent: "tab-files",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title:
              role === "CONSULTANT"
                ? "Files"
                : "Files (Shared with Consultant)",
            titleOrder: 5,
            withBorder: false,
            minHeight: "0px",
            mt: 20,
            mb: 20,
          },
        },
        {
          key: "section-file-groups",
          parent: "tab-files",
          type: ENUM_FORM_LAYOUT_CONTAINER.EMPTY,
          props: {},
        },

        {
          key: "section-client-relationships",
          parent: "tab-relationships",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Client Relationships",
            withBorder: false,
            mt: 0,
            mb: 20,
          },
        },
        {
          key: "section-risk-stats",
          parent: "tab-risk",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Risk Summary",
            minHeight: "0px",
          },
        },

        {
          key: "section-risk-source-of-fund",
          parent: "tab-risk",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Source of Fund",
            minHeight: "0px",
          },
        },
        {
          key: "section-risk-annual-income",
          parent: "tab-risk",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Annual Income",
            minHeight: "0px",
          },
        },
        {
          key: "section-risk-current-assets",
          parent: "tab-risk",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Current Assets",
            minHeight: "0px",
          },
        },

        {
          key: "section-risk-pep",
          parent: "tab-risk",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "PEP",
          },
        },

        {
          key: "section-professional-investor",
          parent: "tab-risk",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Professional Investor",
          },
        },

        {
          key: "section-risk-aml",
          parent: "tab-risk",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "AML",
          },
        },
        {
          key: "section-risk-rpq",
          parent: "tab-risk",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "RPQ",
          },
        },
      ],

      fields: [
        {
          name: "display-basic-info",
          parent: "section-basic-info",
          component: ClientBasicInfo,
          props: {},
        },
        {
          name: "order-summary",
          parent: "section-order-summary",
          component: ClientOrderSummary,
          props: {},
        },
        {
          name: "nameCard",
          component: NameCard,
          parent: "section-name-card",
          props: {},
        },
        {
          name: "debitNotes",
          component: ClientDebitNoteHistory,
          parent: "section-debit-note-history",
          props: {},
        },
        {
          name: "orders",
          component: ClientOrderHistory,
          parent: "section-order-history",
          props: {},
        },

        {
          name: "creditTransactions",
          component: ClientCreditTransaction,
          parent: "section-credit-transaction",
          props: {},
        },
        {
          name: "phones",
          parent: "tab-phones",
          component: PhoneNumberList,
          props: {
            readOnly: isConsultant,
          },
        },
        {
          name: "emails",
          parent: "tab-emails",
          component: EmailList,
          props: {
            readOnly: isConsultant,
          },
        },
        {
          name: "addresses",
          parent: "tab-addresses",
          component: AddressList,
          props: {
            readOnly: isConsultant,
          },
        },
        {
          name: "contacts",
          component: ContactList,
          parent: "tab-related-persons",
          props: {
            phoneField: "tel",
            readOnly: isConsultant,
          },
        },
        {
          name: "consultantList",
          component: ConsultantList,
          parent: "section-consultant-info",
          props: {
            field: "consultants",
            mt: 20,
            showClientTransfer: true,
          },
        },

        {
          name: "clientActionList",
          component: ClientActionList,
          parent: "section-action-list",
          props: {
            role: userRoleName,
          },
        },

        {
          name: "files",
          component: FileList,
          parent: "section-files",
          props: {
            disabled: isConsultant || accessRight !== 15,
          },
        },
        {
          name: "specimenSignatureFiles",
          component: FileList,
          parent: "section-specimen-signature",
          props: {
            disabled: isConsultant || accessRight !== 15,
            defaultDisplayMode: K_FILE_LIST_DISPLAY_MODE.GRID,
            uploadLabel: "Upload Chop / Signature",
            accept: IMAGE_MIME_TYPE,
          },
        },
        {
          name: "consultantFiles",
          component: FileList,
          parent: "section-consultant-files",
          props: {
            disabled: isConsultant || accessRight !== 15,
          },
        },
        {
          name: "fileGroups",
          component: UserFileGroupList,
          parent: "section-file-groups",
          props: {
            disabled: isConsultant || accessRight !== 15,
            consultantView: role === "CONSULTANT",
          },
        },

        {
          name: "clientRelationships",
          component: ClientRelationshipList,
          parent: "section-client-relationships",
          props: {
            role: role,
          },
        },

        {
          name: "riskStats",
          component: ClientRiskStats,
          parent: "section-risk-stats",
          props: {},
        },

        {
          name: "pep.isPep",
          component: Checkbox,
          parent: "section-risk-pep",
          props: {
            label: "is Political Exposure Person? (PEP)",
            type: "checkbox",
            disabled: isConsultant,
          },
        },
        {
          name: "pep.remark",
          component: TextInput,
          parent: "section-risk-pep",
          props: {
            label: "Remark",
            disabled: isConsultant,
          },
        },

        {
          name: "professionalInvestor",
          component: Checkbox,
          parent: "section-professional-investor",
          props: {
            label: "Professional Investor",
            type: "checkbox",
            disabled: isConsultant,
          },
        },

        {
          name: "incomeSource",
          component: ClientIncomeSource,
          parent: "section-risk-source-of-fund",
          props: {
            disabled: isConsultant,
          },
        },
        {
          name: "annualIncome",
          component: ClientAnnualIncome,
          parent: "section-risk-annual-income",
          props: {
            disabled: isConsultant,
          },
        },
        {
          name: "currentAssets",
          component: ClientCurrentAssets,
          parent: "section-risk-current-assets",
          props: {
            label: "Current Assets Amount",
            disabled: isConsultant,
          },
        },
        {
          name: "majorInvestmentPortfolio",
          component: ClientMajorInvestmentPortfolio,
          parent: "section-risk-current-assets",
          props: {
            label: "Major Investment Portfolio",
            disabled: isConsultant,
            mt: 20,
          },
        },
        {
          name: "riskAML",
          component: ClientAMLList,
          parent: "section-risk-aml",
          props: {
            disabled: isConsultant,
          },
        },

        {
          name: "rpqAlert",
          component: ClientRpqAlert,
          parent: "section-alert",
          props: {},
        },
        {
          name: "riskRPQ",
          component: ClientRPQList,
          parent: "section-risk-rpq",
          props: {
            disabled: isConsultant,
          },
        },
        {
          name: "clientRemarks",
          component: RemarkList,
          parent: "section-remarks",
          props: {
            scrollHeight: 100,
            disabled: isConsultant,
          },
        },
        {
          name: "log",
          component: ActivityLog,
          parent: "section-client-log",
          props: {},
        },

        {
          name: "followup",
          component: FollowupActionTable,
          parent: "section-followup",
          props: {},
        },
        {
          name: "confirm",
          component: FormConfirmation,
          parent: "section-confirm",
          props: {},
        },
      ],
    },
  };

  const tableSchema = {
    showActionColumn: true,
    preQuery,
    uniqueFields: ["name"],
    copyToNewFields: [
      "status",
      "type",
      "id",
      "idType",
      "idIssueCountry",
      "idDocExpiryDate",
      "consultants",
      "name",
      "email",
      "phone",
      "isTransfer",
      "transferFrom",
      "birthday",
      "placeOfBirth",
      "language",
      "gender",
      "maritalStatus",
      "age",
      "occupation",
      "bussinessNature",
      "educationLevel",
      "nationality",
      "residentialCountry",
      "vulnerable",
      "yearlySalary",
      "salaryPayRatio",
      "aml",
      "rpq",
      "pep",
      "professionalInvestor",
      "piEffectiveDate",
      "piAnnualConfirmationDate",
      "piExpirationReminderDate",
      "piTerminationDate",
      "remark",
      "clientRemarks",
      "mailType",
      "accountCloseDate",
      "addresses",
      "consultantList",
      "clientActionList",
      "files",
      "specimenSignatureFiles",
      "consultantFiles",
      "fileGroups",
      "contactPerson",
    ],
    columns: [
      {
        field: "code",
        sortable: true,
        headerName: "Code",
        width: 140,
        cellRender: (col, data) => (
          <>
            <Group spacing={4} noWrap>
              {data.status ? cellRender.Status(data.status) : null}
              {data.isTransfer ? (
                <Badge size="xs" color={"red"} radius={0}>
                  Transfer
                </Badge>
              ) : (
                <Badge size="xs" color={"teal"} radius={0}>
                  Direct
                </Badge>
              )}
              {data.professionalInvestor && (
                <Badge size="xs" color="yellow" radius={0}>
                  PI
                </Badge>
              )}
              <McaApprovalListBadge entity={data} />
            </Group>
            <Text size="xs" weight={500}>
              {data.code}
            </Text>
            <Text size="xs" color="dimmed">
              {data.createdAt
                ? moment(data.createdAt).format("YYYY-MM-DD")
                : ""}
            </Text>
          </>
        ),
      },
      {
        field: "name",
        sortable: true,
        headerName: "Client",
        width: 400,
        cellRender: (col, data) =>
          cellRender.ClientInfo(data, false, false, true),
      },

      // {
      //   field: "id",
      //   sortable: true,
      //   headerName: "ID No.",
      // },
      {
        field: "contactPerson",
        sortable: true,
        headerName: "Contact Person",
        width: 180,
      },
      {
        field: "credit.balance",
        sortable: true,
        headerName: "Credit",
        width: 120,
        cellRender: (col, data) =>
          cellRender.Currency(data.credit?.balance ?? 0),
      },

      {
        field: "consultants",
        sortable: true,
        headerName: "Consultants",
        width: 240,
        cellRender: (col, data) => cellRender.Users(data.consultants),
      },

      // {
      // 	field: "order",
      // 	headerName: "Orders",
      // 	sortable: true,
      // 	cellRender: (col, data) => data.orders.length,
      // },
      // { field: "phone", sortable: true },

      {
        field: "_filterStatus",
        headerName: "Status",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "client",
          field: "status",
          label: "Client Status",
          width: 250,
          height: 300,
        },
      },
      {
        field: "_filterConsultants",
        headerName: "Consultant",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "client",
          field: "consultants",
          label: "Consultant",
          width: 300,
          height: 320,
          populate: {
            model: "User",
            select: "name cname staffId aliasName",
            labelField: "name",
            sort: { name: 1 },
          },
        },
      },
      {
        field: "_filterClientType",
        headerName: "Type",
        filterOnly: true,
        filterable: true,
        filter: {
          apiEntity: "client",
          field: "type",
          label: "Type",
          width: 250,
          height: 300,
        },
      },
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [
          {
            name: "edit",
            icon: IconPencil,
            props: {
              //   color:"red"
            },
          },
        ],
        actionMenu:
          accessRight === 15
            ? [
                {
                  name: "copy",
                  label: "Copy",
                  icon: IconCopy,
                  props: {
                    // color: "blue",
                  },
                },
                {
                  name: "delete",
                  label: "Delete",
                  icon: IconTrash,
                  props: {
                    color: "red",
                  },
                },
              ]
            : null,
      },
    ],
    searchableFields: [
      "code",
      "name",
      "cname",
      "email",
      "phone",
      "id",
      "status",
      // "consultants.name",
      // "consultants.cname",
      "consultants.staffId",
      "consultants.aliasName",
      "parentCompany.name",
    ],
    select:
      "code name cname consultants email phone type credit contactPerson isTransfer transferFrom professionalInvestor status createdAt mcaRequestId mcaPending rpqStatus rpq",
  };

  const addFormSchema = {
    title: "New Client",
    promptAfterAdd: true,
    display: {
      mode: ENUM_FORM_DISPLAY.MODAL,
      size: "1000px", //xs, sm, xl  ....100%
      // position:"right"
    },

    showSaveBar: false,
    apiEntity,
    initialValues,

    validate: buildIndividualClientValidate(t),

    layout: {
      containers: [
        {
          key: "t1",
          parent: null,
          type: ENUM_FORM_LAYOUT_CONTAINER.WIZARD,
          props: {
            variant: "default", //default | pills | outline
            defaultValue: "Type",
            orientation: "vertical",
            style: { minHeight: "400px" },
          },
          steps: [
            { key: "t1-1", label: "Type", icon: IconSettings },
            { key: "t1-2", label: "Client ID", icon: IconSettings },
            { key: "t1-3", label: "Basic Info", icon: IconSettings },
            { key: "t1-6", label: "Contact Info", icon: IconPhone },
            { key: "t1-4", label: "Consultant", icon: IconSettings },
            { key: "t1-5", label: "Confirm", icon: IconSettings },
          ],
        },
        {
          key: "section-type",
          parent: "t1-1",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Client Types",
            description: "Please choose type for client",
            withBorder: false,
            ml: 10,
            mt: 0,
          },
        },
        {
          key: "section-ID",
          parent: "t1-2",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Main ID",
            titleOrder: 5,
            description: "Please enter the primary identity document",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-other-ids",
          parent: "t1-2",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: isOtherIdsSectionVisible,
          props: {
            title: "Other IDs",
            titleOrder: 5,
            description: "Add additional identity documents for this client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-basicInfo",
          parent: "t1-3",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: (formValues) => {
            return formValues.type != "CORPORATE";
          },
          props: {
            title: "Basic Info",
            titleOrder: 5,
            description: "Please enter basic info of client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "section-basicInfo-corporate",
          parent: "t1-3",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          visibleOnly: (formValues) => {
            return formValues.type == "CORPORATE";
          },
          props: {
            title: "Basic Info",
            titleOrder: 5,
            description: "Please enter basic info of client",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "basicInfo-g",
          parent: "section-basicInfo",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            {
              key: "basicInfo-g1",
              props: {
                span: 6,
              },
            },
            {
              key: "basicInfo-g2",
              props: {
                span: 6,
              },
            },
          ],
        },
        {
          key: "basicInfo-g1-top",
          parent: "basicInfo-g1",
          type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
        },
        {
          key: "name-fields-g",
          parent: "basicInfo-g1",
          type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
          cols: [
            {
              key: "name-fields-surname",
              props: {
                span: 4,
              },
            },
            {
              key: "name-fields-given",
              props: {
                span: 8,
              },
            },
          ],
        },
        {
          key: "basicInfo-g1-bottom",
          parent: "basicInfo-g1",
          type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
        },

        {
          key: "section-consultant",
          parent: "t1-4",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Consultant",
            titleOrder: 5,
            description: "Please assign the corresponding consultant",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "section-confirm",
          parent: "t1-5",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Confirm",
            titleOrder: 3,
            description:
              "Please confirm to add client or you can go back to revise.",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },

        {
          key: "section-contact-wizard",
          parent: "t1-6",
          type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
          props: {
            title: "Contact Info",
            titleOrder: 3,
            description: "Please fill in phone numbers, emails, and addresses",
            withBorder: false,
            ml: 10,
            mt: 0,
            mb: 10,
          },
        },
        {
          key: "tab-contact-wizard",
          parent: "section-contact-wizard",
          type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
          props: {
            variant: "outline",
            size: "sm",
            defaultValue: "Phone Numbers",
          },
          tab: [
            {
              key: "tab-wizard-phones",
              label: "Phone Numbers",
            },
            {
              key: "tab-wizard-emails",
              label: "Emails",
            },
            {
              key: "tab-wizard-addresses",
              label: "Address",
            },
            {
              key: "tab-wizard-related-persons",
              label: "Related Person Info",
            },
          ],
        },

        // {
        //     key:'basicInfo-address-g',
        //     parent: "section-address",
        //     type:ENUM_FORM_LAYOUT_CONTAINER.GRID,
        //     cols: [
        //         {
        //             key:"basicInfo-address-g-1",
        //             props:{
        //                 span: 12,
        //                 mt:0
        //             }
        //         },
        //         {
        //             key:"basicInfo-address-g-2",
        //             props:{
        //                 span: 6,
        //             }
        //         },
        //         {
        //             key:"basicInfo-address-g-3",
        //             props:{
        //                 span: 6,
        //             }
        //         },

        //     ]
        // },
      ],

      fields: [
        {
          name: "type",
          component: Chips,
          parent: "section-type",
          props: {
            required: true,
            labelOrder: 5,
            description: "Please Select the type of client.",
            items: DATA_CLIENT_TYPES,
            defaultValue: "INDIVIDUAL",
            multiple: false,
          },
        },

        // {
        // 	name: "id",
        // 	component: TextInput,
        // 	parent: "section-ID",
        // 	props: {
        // 		required: true,
        // 		// label: "Please enter client ID ( HKID / PASSPORT / BR )",
        // 		placeholder: "HKID / PASSPORT / BR / Mainland Travel Permit",
        // 		mt: "xl",
        // 	},
        // },
        {
          name: "noIdProvided",
          component: NoIdProvidedCheckbox,
          parent: "section-ID",
          props: {
            label: "No ID provided",
            type: "checkbox",
          },
        },
        {
          name: "idType",
          component: Select,
          parent: "section-ID",
          visibleOnly: (formValues) => isMainIdRequired(formValues.idType),
          props: {
            required: (formValues) => isMainIdRequired(formValues.idType),
            label: "ID Type",
            placeholder: "",
            data: DATA_ID_TYPE,
            defaultValue: "HKID",
            searchable: true,
            onDateChange: (value, form) =>
              applyDefaultIdIssueCountry(value, form),
          },
        },
        {
          name: "id",
          component: UniqueTextInput,
          parent: "section-ID",
          visibleOnly: (formValues) => isMainIdRequired(formValues.idType),
          props: {
            required: (formValues) => isMainIdRequired(formValues.idType),
            uniqueEntity: "client",
            errorMessage:
              "Servicing Rights of the client are already taken. Please check the with operation team for details.",

            label: "ID No.",
            placeholder: "HKID / PASSPORT / BR / Mainland Travel Permit",
            mt: 0,
          },
        },

        {
          name: "idIssueCountry",
          component: Select,

          parent: "section-ID",
          visibleOnly: (formValues) => isMainIdRequired(formValues.idType),
          props: {
            required: (formValues) => isMainIdRequired(formValues.idType),
            label: "Issue Country",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
          },
        },
        {
          name: "idDocExpiryDate",
          component: DatePicker,
          type: "datePicker",
          parent: "section-ID",
          visibleOnly: (formValues) =>
            isMainIdRequired(formValues.idType) && formValues.idType !== "HKID",
          props: {
            required: (formValues) => isIdExpiryRequired(formValues.idType),
            label: "Expiry",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },
        {
          name: "otherIds",
          component: ClientIdentityList,
          parent: "section-other-ids",
          props: {
            hideTitle: true,
          },
        },

        {
          name: "salutation",
          component: Select,
          parent: "basicInfo-g1-top",
          props: {
            required: true,
            label: "Saluation",
            placeholder: "",
            data: DATA_SALUATATION,
            defaultValue: "Mr.",
            searchable: true,
          },
        },
        {
          name: "surname",
          component: TextInput,
          parent: "name-fields-surname",
          props: {
            required: true,
            placeholder: "Chan / Au Yeung",
            label: "Surname",
          },
        },
        {
          name: "givenName",
          component: TextInput,
          parent: "name-fields-given",
          props: {
            required: true,
            placeholder: "Tai Man / Man Tat",
            label: "Given Name",
          },
        },
        {
          name: "name",
          component: TextInput,
          parent: "section-basicInfo-corporate",
          props: {
            required: true,
            placeholder: "Company ABC Ltd",
            label: "English Name (Company Name)",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "basicInfo-g1-bottom",
          props: {
            // required: true,
            placeholder: "陳大文",
            label: "Chinese Name",
          },
        },
        {
          name: "cname",
          component: TextInput,
          parent: "section-basicInfo-corporate",
          props: {
            // required: true,
            placeholder: "大大有限公司",
            label: "Chinese Name (Company Name)",
          },
        },
        {
          name: "birthday",
          component: DatePicker,
          type: "datePicker",
          parent: "basicInfo-g1-bottom",
          props: {
            required: true,
            label: "Date of Birth",
            zIndex: 1000,
            placeholder: "Pick Date",
            allowFreeInput: true,
          },
        },

        {
          name: "phones",
          component: PhoneNumberList,
          parent: "tab-wizard-phones",
          props: {},
        },
        {
          name: "emails",
          component: EmailList,
          parent: "tab-wizard-emails",
          props: {},
        },
        {
          name: "addresses",
          component: AddressList,
          parent: "tab-wizard-addresses",
          props: {},
        },
        {
          name: "contacts",
          component: ContactList,
          parent: "tab-wizard-related-persons",
          props: {
            phoneField: "tel",
          },
        },

        {
          name: "contactPerson",
          component: TextInput,
          parent: "section-basicInfo-corporate",
          props: {
            placeholder: "Kenny Chan",
            label: "Contact Person",
          },
        },
        {
          name: "parentCompany",
          component: RemoteSelect2,
          parent: "section-basicInfo-corporate",
          props: {
            label: "Parent Company",
            placeholder: "",
            apiEntity: "client",
            valueField: "_id",
            labelField: "name",
            searchFields: ["name"],
            pageSize: 2000,
            sort: {
              by: "name",
              order: "asc",
            },
          },
        },

        {
          name: "gender",
          component: Select,
          parent: "basicInfo-g1-bottom",
          props: {
            required: true,
            label: "Gender",
            placeholder: "",
            data: DATA_GENDERS,
            defaultValue: "Male",
            searchable: true,
          },
        },

        {
          name: "occupation",
          component: TextInput,
          parent: "basicInfo-g1-bottom",
          props: {
            placeholder: "Teacher",
            label: "Occupation",
            defaultValue: "Other",
          },
        },
        {
          name: "educationLevel",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            label: "Education Level",
            placeholder: "",
            data: DATA_EDUCATION_LEVEL,
            searchable: true,
            clearable: true,
          },
        },
        {
          name: "language",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Language",
            placeholder: "",
            data: DATA_LANGUAGE,
            defaultValue: "CANTONESE",
            searchable: true,
          },
        },

        {
          name: "maritalStatus",
          component: Select,
          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Marital Status",
            placeholder: "",
            data: DATA_MARIAGE_STATUS,
            defaultValue: "Single",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "nationality",
          component: Select,

          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Nationality",
            placeholder: "",
            data: DATA_NATIONALITY,
            defaultValue: "Chinese",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "residentialCountry",
          component: Select,

          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Residential Country",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "placeOfBirth",
          component: Select,

          parent: "basicInfo-g2",
          props: {
            required: true,
            label: "Country of Birth",
            placeholder: "",
            data: DATA_COUNTRY,
            defaultValue: "Hong Kong",
            searchable: true,
            clearable: true,
          },
        },

        {
          name: "consultants",
          component: RemoteSelect2,
          parent: "section-consultant",
          props: {
            apiEntity: "user",
            required: true,
            placeholder: "Assign consultant(s)",
            valueField: "_id",
            labelField: "name",
            labelRender: (data) => formatTeamUserLabel(data),
            multiple: true,
            preQuery: {
              $and: [{ isConsultant: true }, { status: "ACTIVE" }],
            },
            searchFields: ["name", "staffId", "cname", "email", "aliasName"],
            pageSize: 2000,
            sort: {
              by: "staffId",
              order: "asc",
            },
          },
        },

        {
          name: "consultantList",
          component: ConsultantList,
          parent: "section-consultant",
          props: {
            field: "consultants",
            mt: 0,
          },
        },
        {
          name: "confirm",
          component: FormConfirmation,
          parent: "section-confirm",
          props: {
            validateOnMount: true,
            fieldLabels: CLIENT_CONFIRM_FIELD_LABELS,
          },
        },
      ],
    },
  };

  const mergedEditFormSchema = useExtraFieldsFormSchema(
    editFormSchema,
    "Client",
    {},
    [isConsultant],
  );

  const formSchema = useMemo(
    () => ({
      edit: mergedEditFormSchema,
      add: addFormSchema,
    }),
    [mergedEditFormSchema, addFormSchema],
  );

  // Consultant users should use the consultant portal; staff roles (including
  // Administrator) may open operation/management/account routes as-is.
  useEffect(() => {
    if (userRoleName !== "CONSULTANT") return;

    if (currentURL.includes("operation")) {
      tabNav.replace({
        pathname: currentURL.replace("operation", "consultant"),
        label: "Client",
      });
      return;
    }

    if (currentURL.includes("management")) {
      tabNav.replace({
        pathname: currentURL.replace("management", "consultant"),
        label: "Client",
      });
    }
  }, [userRoleName, currentURL, tabNav]);

  useEffect(() => {
    revisePreQuery();
  }, [isConsultant]);

  const revisePreQuery = async () => {
    if (!isConsultant) {
      setPreQuery({});
      initialValues.consultants = [];
      return;
    }

    if (process.env.REACT_APP_DEBIT_NOTE_SEARCH_TEAM === "true") {
      const teamMembersIds = await api.User.getAllTeamMembers(auth().id);
      setPreQuery({
        consultants: { $in: [auth().id, ...teamMembersIds] },
      });
    } else {
      setPreQuery({ consultants: auth().id });
    }
    initialValues.consultants = [];
  };

  return (
    <RiskControlSettingsProvider>
      {/* REACT_APP_CLIENT_INIT_STATUS: {process.env.REACT_APP_CLIENT_INIT_STATUS} */}
      {/* {isConsultant ? "Consultant" : "Ops"} */}
      {preQuery && (
        <PageList
          title={"Client"}
          titleField={"name"}
          setDocumentTitle={true}
          apiEntity={apiEntity}
          initSort={{ by: "code", order: "desc" }}
          tableSchema={tableSchema}
          formSchema={formSchema}
          searchByTextField={false}
          importVariants={[
            {
              key: "clientData",
              label: "Client Data",
              templateDownloads: [
                {
                  url: clientImportTemplateUrl,
                  text: "Download Template",
                },
              ],
            },
            {
              key: "clientLogs",
              label: "Client Logs",
              templateDownloads: [
                {
                  url: clientLogImportTemplateUrl,
                  text: "Download Template",
                },
              ],
              onImport: (file, _opts, api) => api.Client.importLogBatch(file),
            },
          ]}
          stats={{
            component: ClientPageStats,
            props: {
              mb: 35,
              role,
              preQuery,
            },
          }}
        />
      )}
    </RiskControlSettingsProvider>
  );
}
